import React, { useEffect, useState } from "react";
import { Download, FileText, FileSpreadsheet, BookMarked, File, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";

const TYPE_ICON = { SOP: FileText, Template: FileSpreadsheet, Reference: BookMarked, Transcript: File };

function toDownloadHref(url) {
  if (!url) return "#";
  if (/^https?:\/\//i.test(url)) return url;
  const tok = localStorage.getItem("kp_token") || "";
  const base = API.replace(/\/api$/, "");
  const sep = url.includes("?") ? "&" : "?";
  return `${base}${url}${sep}auth=${encodeURIComponent(tok)}`;
}

export default function Downloads() {
  const { activeProgram } = useAuth();
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/downloads", { params: { program: activeProgram } }).then((r) => setItems(r.data)).catch(() => {});
  }, [activeProgram]);

  return (
    <div>
      <PageHeader
        eyebrow="The Library · Downloads"
        title="Downloads & Forms"
        subtitle="SOP PDFs, templates and reference guides — grab what you need."
        testid="downloads-header" />

      {items.length === 0 ? (
        <EmptyState icon={Download} title="Nothing to download yet" hint="Downloadable resources will appear here." />
      ) : (
        <div className="rounded-2xl border border-border bg-card divide-y divide-border overflow-hidden stagger" data-testid="downloads-list">
          {items.map((d) => {
            const Icon = TYPE_ICON[d.type] || File;
            const href = toDownloadHref(d.url);
            return (
              <a href={href} target="_blank" rel="noreferrer" key={d.id}
                data-testid={`download-${d.id}`}
                className="group flex items-center gap-5 p-5 hover:bg-secondary/30 transition-colors">
                <div className="w-11 h-11 rounded-xl bg-secondary/60 flex items-center justify-center shrink-0 group-hover:bg-primary transition-all">
                  <Icon className="w-5 h-5 text-primary group-hover:text-primary-foreground transition-colors" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-display text-lg text-foreground truncate leading-tight">{d.title}</p>
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap font-mono uppercase tracking-wider">
                    <span>{d.type}</span>
                    <span className="opacity-40">·</span>
                    <span>{d.size}</span>
                    {d.last_updated && <><span className="opacity-40">·</span><span>Updated {d.last_updated}</span></>}
                    {d.attachment && <><span className="opacity-40">·</span><span className="text-primary">From article</span></>}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 text-sm font-semibold text-primary group-hover:translate-x-1 transition-transform">
                  Download <ArrowUpRight className="w-4 h-4" />
                </div>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
